import { FormProvider, SubmitHandler, useForm, useWatch } from "react-hook-form";
import { mixed, object, SchemaOf, string } from "yup";
import { yupResolver } from "@hookform/resolvers/yup";
import { DocumentationCategory } from "../../../common/store/generalTypes";
import { LBtn, LInputField } from "@components";
import { DefaultErrorMessages } from "@helpers/formValidationHelper";
import { useApi } from "@services/useApi";
import { LSelect } from "@components/LSelect";
import { useModal } from "@services/ModalContext";
import { isEmpty } from "lodash";

interface DocumentationForm {
    title: string;
    description: string;
    type: string;
    file?: File[];
}

const formSchema: SchemaOf<DocumentationForm> = object({
    title: string().required(DefaultErrorMessages.required),
    description: string().required(DefaultErrorMessages.required),
    type: string().required(DefaultErrorMessages.required),
    file: mixed().when("type", (val) => {
        if (val !== "faq") {
            return mixed()
                .test("required", DefaultErrorMessages.required, (file) => {
                    if (file.length) {
                        return true;
                    }
                    return false;
                })
                .test("is-valid-type", `* type it is not valid for ${val} file`, (file) => {
                    if (val === "video" && !isEmpty(file)) {
                        return file[0].type.match("video.*");
                    }

                    return true;
                });
        }
        return mixed();
    })
});

export const CreateDocumentation = ({ onCreated }: CreateDocumentationProps): JSX.Element => {
    const validateMethods = useForm<DocumentationForm>({
        resolver: yupResolver(formSchema)
    });
    const type = useWatch({ control: validateMethods.control, name: "type", defaultValue: "Video" });
    const { closeModal } = useModal();
    const { loading, createDocumentation } = useApi();

    const formSubmitHandler: SubmitHandler<DocumentationForm> = async (data: DocumentationForm) => {
        let file;
        if (!isEmpty(data.file)) {
            file = data.file![0];
        }

        await createDocumentation(data.title, data.description, data.type as DocumentationCategory, file);

        onCreated?.();
        closeModal();
    };

    return (
        <FormProvider {...validateMethods}>
            <form onSubmit={validateMethods.handleSubmit(formSubmitHandler)}>
                <LSelect
                    label="Type"
                    inputName="type"
                    options={[
                        { name: "Video", value: DocumentationCategory.Video },
                        { name: "Document", value: DocumentationCategory.Document },
                        { name: "FAQ", value: DocumentationCategory.Faq }
                    ]}
                    defaultValue="Video"
                />
                <LInputField label="Title" inputName="title" placeholder="Enter title" defaultValue="" />
                <LInputField
                    label="Description"
                    inputName="description"
                    placeholder="Enter description"
                    defaultValue=""
                />
                {type !== "faq" && (
                    <LInputField
                        label="File"
                        inputName="file"
                        type="file"
                        placeholder={`Select a ${type}`}
                        defaultValue=""
                    />
                )}
                <div className="pt-2">
                    <div className="columns">
                        <div className="column" />
                        <div className="column is-4">
                            <LBtn variation="outline" fullWidth onClick={closeModal}>
                                Cancel
                            </LBtn>
                        </div>
                        <div className="column is-1" />
                        <div className="column is-4">
                            <LBtn fullWidth type="submit" loading={loading}>
                                Save
                            </LBtn>
                        </div>
                        <div className="column" />
                    </div>
                </div>
            </form>
        </FormProvider>
    );
};

interface CreateDocumentationProps {
    onCreated?: () => void;
}
